import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { useRequest } from "hooks/useRequest";
import { sortFolderRecordsByName } from "./utils";
import type { FloorPlanFolderRecord } from "../../utils/types";
import type {
  UseFloorPlanFolderRecordsArgs,
  UseFloorPlanFolderRecordsResult,
} from "./types";

export const useFloorPlanFolderRecords = ({
  projectId,
}: UseFloorPlanFolderRecordsArgs): UseFloorPlanFolderRecordsResult => {
  const { t } = useTranslation();

  const {
    data: folderRecords = [],
    isLoading: isInitialLoading,
    mutateData: mutateFolderRecords,
  } = useRequest<FloorPlanFolderRecord[], []>(
    async () => {
      try {
        const folders: FloorPlanFolderRecord[] =
          await api.getQuantitySurveyorFloorPlanFolders(projectId);
        return sortFolderRecordsByName(folders);
      } catch {
        // Without folders every plan shows at the root; the page must still open.
        toast.error(t("smartSurveyor.folders.loadFailed"));
        return [];
      }
    },
    { manual: false, defaultParams: [] },
  );

  // A folder already in the list (one the import reused) replaces its record instead of showing twice.
  const upsertFolderRecords = useCallback(
    (upsertedFolders: FloorPlanFolderRecord[]) => {
      const upsertedFolderIds = new Set(
        upsertedFolders.map((folder) => folder.id),
      );
      mutateFolderRecords({
        updater: (folders) =>
          sortFolderRecordsByName([
            ...folders.filter((folder) => !upsertedFolderIds.has(folder.id)),
            ...upsertedFolders,
          ]),
      });
    },
    [mutateFolderRecords],
  );

  const renameFolderRecord = useCallback(
    (folderId: number, name: string) => {
      mutateFolderRecords({
        updater: (folders) =>
          sortFolderRecordsByName(
            folders.map((folder) =>
              folder.id === folderId ? { ...folder, name } : folder,
            ),
          ),
      });
    },
    [mutateFolderRecords],
  );

  const removeFolderRecord = useCallback(
    (folderId: number) => {
      mutateFolderRecords({
        updater: (folders) =>
          folders.filter((folder) => folder.id !== folderId),
      });
    },
    [mutateFolderRecords],
  );

  return {
    isInitialLoading,
    folderRecords,
    upsertFolderRecords,
    renameFolderRecord,
    removeFolderRecord,
  };
};
